import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import {
  linkedAccountsQuery,
  patientQuery,
} from '@patient-ui/patient-web/store';
import { IPatient, LinkedAcctStatus } from '@patient-ui/shared/models';

import { AppointmentSlot, LabLocation, Patient } from '../models';
import { BookingSessionStore } from '../stores';
import { FindALabResultsTrackingService } from './find-a-lab-results-tracking.service';
import { HoldAppointmentService } from './hold-appointment.service';

export interface SlotSelectionError {
  locationId: number;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class BookingFlowService {
  constructor(
    private holdAppointmentService: HoldAppointmentService,
    private bookingStore: BookingSessionStore,
    private findALabResultsTracking: FindALabResultsTrackingService,
    private router: Router,
    private store: Store
  ) {}

  handleSlotSelection(
    slot: AppointmentSlot,
    location: LabLocation,
    skipNavigation = false
  ): Observable<void> {
    // Store selected slot and location
    this.bookingStore.setSelectedSlot(slot);
    this.bookingStore.setSelectedLocation(location);

    // Normalize datetime format - remove seconds if present
    // API expects format: "YYYY-MM-DDTHH:mm" not "YYYY-MM-DDTHH:mm:ss"
    const normalizedDateTime = this.normalizeDateTime(slot.dateTime);

    // Call hold API
    const holdRequest = {
      locatorId: location.id,
      serviceType: slot.serviceId,
      apptDateTime: normalizedDateTime,
    };

    return this.holdAppointmentService.holdAppointment(holdRequest).pipe(
      tap((response) => {
        // Store hold confirmation
        this.bookingStore.setHoldAppointment(
          response.confirmationCode,
          response.expiresAt
        );
        this.findALabResultsTracking.trackStandardSlotHold({
          locationId: location.id,
          slot: normalizedDateTime,
          serviceId: slot.serviceId,
        });
      }),
      switchMap(() => {
        // Skip navigation if requested (e.g., modifying existing appointment)
        if (skipNavigation) {
          return of(undefined);
        }

        // Check if we should return to review page (editing from review)
        const session = this.bookingStore.getCurrentState();
        const shouldReturnToReview = session.returnToReview;

        if (shouldReturnToReview) {
          // Clear flag and navigate to review page
          this.bookingStore.setReturnToReview(false);
          this.router.navigate(['/portal/appointments/book/review']);
          return of(undefined);
        }

        // Check if patient is already selected (Make Appointment flow)
        if (session.selectedPatient && session.selectedPatient.id !== 0) {
          // Patient already selected, skip patient-selection page

          if (session.selectedPatient.isPrimaryAccountHolder) {
            // Primary account holder - go to contact preferences
            this.router.navigate([
              '/portal/appointments/book/contact-preferences',
            ]);
            return of(undefined);
          } else {
            // Linked account (both adults and minors) - go to contact preferences
            // The contact-preferences component will determine whether to show the form or skip to review:
            // - Linked adults: Always skip to review
            // - Linked minors: Show form if not opted in, skip to review if opted in
            this.router.navigate([
              '/portal/appointments/book/contact-preferences',
            ]);
            return of(undefined);
          }
        }

        // Patient not selected yet (Find a Lab flow)
        // Check if user has linked accounts
        return combineLatest([
          this.store
            .select(patientQuery.selectPrimaryAccountHolder)
            .pipe(take(1)),
          this.store
            .select(linkedAccountsQuery.selectProcessedLinkedAccountsList)
            .pipe(take(1)),
        ]).pipe(
          tap(([primaryAccountHolder, linkedAccounts]) => {
            // Filter linked accounts using same logic as patient selection
            const filteredLinkedAccounts = linkedAccounts.filter(
              (account) =>
                account.linkedAcctToCurrentUserStatus ===
                  LinkedAcctStatus.ACCEPTED ||
                (account.isMinor &&
                  account.currentUserToLinkedAcctStatus ===
                    LinkedAcctStatus.ACCEPTED)
            );

            // If no linked accounts, auto-select primary user and skip patient selection
            if (filteredLinkedAccounts.length === 0 && primaryAccountHolder) {
              const primaryPatientData =
                this.mapIPatientToPatient(primaryAccountHolder);
              this.bookingStore.setSelectedPatient(primaryPatientData);

              // Navigate directly to contact preferences
              this.router.navigate([
                '/portal/appointments/book/contact-preferences',
              ]);
            } else {
              // Navigate to patient selection
              this.router.navigate([
                '/portal/appointments/book/patient-selection',
              ]);
            }
          })
        );
      }),
      map(() => undefined),
      catchError((error) => {
        const parsedError = this.parseHoldError(error);
        const errorObj = {
          locationId: location.id,
          message: parsedError.message,
          is409: parsedError.is409,
        };
        return throwError(errorObj);
      })
    );
  }

  private normalizeDateTime(dateTime: string): string {
    // Remove seconds from datetime if present
    // Convert "2025-12-18T08:30:00" to "2025-12-18T08:30"
    // Already formatted strings like "2025-12-18T08:30" remain unchanged
    if (dateTime.includes(':')) {
      const parts = dateTime.split(':');
      if (parts.length === 3) {
        // Has seconds, remove them
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return dateTime;
  }

  private parseHoldError(error: unknown): { message: string; is409: boolean } {
    const httpError = error as {
      status?: number;
      error?: { status?: string; errors?: Array<{ field: string }> };
    };

    // Check if it's a SlotTaken (409) error
    if (httpError.status === 409) {
      return {
        message:
          'The appointment time you selected is no longer available. Please choose a new one.',
        is409: true,
      };
    }

    // Check for SlotTaken in error response
    if (
      httpError.error &&
      httpError.error.status === 'error' &&
      httpError.error.errors
    ) {
      const slotTakenError = httpError.error.errors.find(
        (err) => err.field === 'SlotTaken'
      );
      if (slotTakenError) {
        return {
          message:
            'The appointment time you selected is no longer available. Please choose a new one.',
          is409: true,
        };
      }
    }

    return {
      message:
        'There was an unexpected error while selecting your appointment. Please try again',
      is409: false,
    };
  }

  private mapIPatientToPatient(iPatient: IPatient): Patient {
    const primaryAddress = iPatient.addresses.find((addr) => addr.isPrimary);
    const primaryPhone = iPatient.phones.find((phone) => phone.isPrimary);
    const primaryEmail = iPatient.emailAddresses.find(
      (email) => email.isPrimary
    );

    return {
      id: iPatient.id || 0,
      firstName: iPatient.firstName,
      lastName: iPatient.lastName,
      dateOfBirth: iPatient.dateOfBirth,
      sex:
        iPatient.gender === 'M' || iPatient.gender === 'MALE'
          ? 'MALE'
          : 'FEMALE',
      isPrimaryAccountHolder: true,
      address: {
        street: primaryAddress?.line1 || '',
        line2: primaryAddress?.line2,
        city: primaryAddress?.city || '',
        state: primaryAddress?.state || '',
        zipCode: primaryAddress?.zipCode || '',
      },
      email: primaryEmail?.address,
      phone: primaryPhone?.number,
      lpid: iPatient.lpid?.toString(),
    };
  }
}
